import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  BoxesIcon,
  CarFrontIcon,
  ClipboardListIcon,
  LayoutDashboardIcon,
  type LucideIcon,
  SearchIcon,
  Settings2Icon,
  ShoppingCartIcon,
  StickyNoteIcon,
  TruckIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Kbd } from "@/components/ui/input";
import { useCurrentVehicle } from "@/lib/current-vehicle";
import { desktop } from "@/lib/desktop";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { useCommandPalette } from "./command-palette";
import { CurrentVehicleSelect } from "./current-vehicle";
import { DemoBanner } from "./demo-banner";
import { GarageMenu } from "./garages";
import { Logo, LogoMark } from "./logo";
import { UserMenu } from "./user-menu";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
  badgeTone?: "missing" | "neutral";
  exact?: boolean;
}

function useNavBadges() {
  const trpc = useTRPC();
  const { vehicleId } = useCurrentVehicle();
  const { data } = useQuery({ ...trpc.overview.queryOptions({ vehicleId }), staleTime: 15_000 });
  return data?.counts;
}

function isActive(pathname: string, item: NavItem) {
  if (item.exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

const isMac =
  desktop?.platform === "darwin" ||
  (typeof navigator !== "undefined" && /Mac/.test(navigator.platform));

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const counts = useNavBadges();
  const palette = useCommandPalette();

  const workshop: NavItem[] = [
    { to: "/", label: t("nav.overview"), icon: LayoutDashboardIcon, exact: true },
    {
      to: "/inventory",
      label: t("nav.inventory"),
      icon: BoxesIcon,
      badge: counts?.parts,
      badgeTone: "neutral",
    },
    {
      to: "/vehicles",
      label: t("nav.vehicles"),
      icon: CarFrontIcon,
      badge: counts?.vehicles,
      badgeTone: "neutral",
    },
    {
      to: "/work-orders",
      label: t("nav.workOrders"),
      icon: ClipboardListIcon,
      badge: counts?.openWorkOrders,
      badgeTone: "neutral",
    },
    {
      to: "/templates",
      label: t("nav.templates"),
      icon: StickyNoteIcon,
      badge: counts?.templates,
      badgeTone: "neutral",
    },
  ];
  const purchasing: NavItem[] = [
    {
      to: "/to-order",
      label: t("nav.toOrder"),
      icon: ShoppingCartIcon,
      badge: counts?.toOrder,
      badgeTone: "missing",
    },
    {
      to: "/orders",
      label: t("nav.orders"),
      icon: TruckIcon,
      badge: counts?.inTransit,
      badgeTone: "neutral",
    },
  ];
  const mobile: NavItem[] = [
    { to: "/", label: t("nav.overview"), icon: LayoutDashboardIcon, exact: true },
    { to: "/inventory", label: t("nav.inventory"), icon: BoxesIcon },
    { to: "/work-orders", label: t("nav.workOrders"), icon: ClipboardListIcon },
    {
      to: "/to-order",
      label: t("nav.toOrder"),
      icon: ShoppingCartIcon,
      badge: counts?.toOrder,
      badgeTone: "missing",
    },
    { to: "/vehicles", label: t("nav.vehicles"), icon: CarFrontIcon },
  ];

  const macDesktop = desktop?.platform === "darwin";

  return (
    <div className="flex min-h-dvh">
      {/* Sidebar (md and up) */}
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r border-border bg-sidebar md:flex lg:w-64">
        <div
          className={cn("flex items-center px-4", macDesktop ? "h-14 pl-20 drag-region" : "h-16")}
        >
          <Link
            to="/"
            className="rounded-lg no-drag outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          >
            {macDesktop ? <LogoMark className="size-6" /> : <Logo />}
          </Link>
        </div>
        <div className="flex flex-col gap-2 px-3 pb-2">
          <GarageMenu />
          <CurrentVehicleSelect />
          <button
            type="button"
            onClick={palette.open}
            className="flex h-9 w-full items-center gap-2 rounded-lg border border-border bg-surface px-2.5 text-sm text-muted-foreground shadow-xs transition hover:border-input hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none"
          >
            <SearchIcon className="size-4" />
            <span className="flex-1 text-left">{t("nav.searchHint")}</span>
            <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
            <Kbd>K</Kbd>
          </button>
        </div>
        <nav className="flex flex-1 scrollbar-thin flex-col gap-5 overflow-y-auto px-3 py-2">
          <NavGroup label={t("nav.workshop")} items={workshop} pathname={pathname} />
          <NavGroup label={t("nav.purchasing")} items={purchasing} pathname={pathname} />
        </nav>
        <div className="flex flex-col gap-1 border-t border-border p-3">
          <SidebarLink
            item={{ to: "/settings", label: t("nav.settings"), icon: Settings2Icon }}
            active={pathname.startsWith("/settings")}
          />
          <UserMenu />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar (phones) */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/85 px-4 pt-[env(safe-area-inset-top)] backdrop-blur-md md:hidden">
          <Link to="/">
            <Logo />
          </Link>
          <div className="flex items-center gap-1">
            <CurrentVehicleSelect variant="icon" />
            <button
              type="button"
              onClick={palette.open}
              aria-label={t("nav.search")}
              className="flex size-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <SearchIcon className="size-5" />
            </button>
            <Link
              to="/settings"
              aria-label={t("nav.settings")}
              className="flex size-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Settings2Icon className="size-5" />
            </Link>
            <UserMenu variant="icon" />
          </div>
        </header>
        {/* Draggable strip for the frameless desktop window */}
        {desktop ? (
          <div className="fixed inset-x-0 top-0 z-20 hidden h-3 drag-region md:block" />
        ) : null}

        <DemoBanner />
        <main className="min-w-0 flex-1">{children}</main>

        {/* Bottom tab bar (phones) */}
        <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden">
          <div className="grid grid-cols-5">
            {mobile.map((item) => {
              const active = isActive(pathname, item);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "relative flex flex-col items-center gap-0.5 pt-2 pb-1.5 text-[10.5px] font-medium",
                    active ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  <span className="relative">
                    <item.icon className="size-[22px]" strokeWidth={active ? 2.2 : 1.8} />
                    {item.badge ? (
                      <span className="absolute -top-1 -right-2.5 min-w-4 rounded-full bg-missing px-1 text-center text-[10px] leading-4 font-semibold text-white">
                        {item.badge}
                      </span>
                    ) : null}
                  </span>
                  <span className="max-w-full truncate px-1">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}

function NavGroup({
  label,
  items,
  pathname,
}: {
  label: string;
  items: NavItem[];
  pathname: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="px-2.5 pb-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
        {label}
      </p>
      {items.map((item) => (
        <SidebarLink key={item.to} item={item} active={isActive(pathname, item)} />
      ))}
    </div>
  );
}

function SidebarLink({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <Link
      to={item.to}
      className={cn(
        "group flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        active
          ? "bg-surface text-foreground shadow-xs ring-1 ring-border"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      <item.icon
        className={cn("size-[18px]", active ? "text-brand" : "")}
        strokeWidth={active ? 2.2 : 1.9}
      />
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge ? (
        <span
          className={cn(
            "min-w-5 rounded-full px-1.5 text-center text-[11px] leading-5 font-semibold tabular",
            item.badgeTone === "missing"
              ? "bg-missing-bg text-missing-fg"
              : "bg-muted text-muted-foreground",
          )}
        >
          {item.badge}
        </span>
      ) : null}
    </Link>
  );
}
